import i18n from '../i18n';
import { isPieceUnit } from './units';

const ALLOWED_DISCOUNT_TYPES = ['fixed', 'percentage'];
const ALLOWED_GST_OPTIONS = ['with_gstn', 'without_gstn', 'percentage', 'none'];

// A pieces line is priced per piece: its piece count (quantity_in_pieces on the
// sale/inquiry forms, `pieces` on the PO form) must be > 0, while its `quantity`
// (the optional kg weight) may be 0 or blank.

const linePieceCount = (item) => (item.quantity_in_pieces ?? item.pieces);
const linePiecesField = (item) =>
  (Object.prototype.hasOwnProperty.call(item, 'pieces') && item.quantity_in_pieces === undefined)
    ? 'pieces' : 'quantity_in_pieces';

/**
 * Validation utilities and helpers
 * - Centralized validators for forms (HSN, Tax, User forms, passwords, etc.)
 * - Provides small input filters and transformation helpers used by form components
 */

const DEFAULT_SYMBOL_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export const PASSWORD_REQUIREMENT_ORDER = [
  'length',
  'lowercase',
  'uppercase',
  'number',
  'symbol',
];

export const stripNonDigits = (value = '') => String(value).replace(/\D/g, '');

export const formatNumericInput = (value = '', { maxLength } = {}) => {
  const digits = stripNonDigits(value);
  if (typeof maxLength === 'number' && maxLength > 0) {
    return digits.slice(0, maxLength);
  }
  return digits;
};

export const isValidEmail = (value = '') => {
  const normalized = String(value).trim();
  if (!normalized) return false;
  return /\S+@\S+\.\S+/.test(normalized);
};

/**
 * Quick boolean check that a value looks like an email address
 * - Used in forms where either email or mobile is allowed
 */

export const isValidPhoneNumber = (value = '', { requiredLength = 10 } = {}) => {
  const digitsOnly = stripNonDigits(value);
  if (!digitsOnly) return false;

  if (Array.isArray(requiredLength)) {
    return requiredLength.includes(digitsOnly.length);
  }

  return digitsOnly.length === requiredLength;
};

/**
 * Validate common vehicle registration number formats (basic check)
 * Accepts patterns like:
 * - 'KA-53-1234', 'KA 53 1234' (State-RTO-Reg)
 * - 'MH-12-AB-1234', 'MH12AB1234', 'MH 12 AB 1234' (State-RTO-Series-Reg)
 * (case-insensitive)
 */
export const isValidVehicleNumber = (value = '') => {
  const v = String(value || '').trim();
  if (!v) return false;
  // Indian vehicle number general pattern: 2 letters, 1-2 digits, optional 1-3 letters, 1-4 digits
  // Support flexible formats: 'RJ14CA4241', 'RJ 14 CA 1424', 'RJ-14-CA-2414', 'KA-53-1234'
  const pattern = /^[A-Z]{2}[-\s]?\d{1,2}([-\s]?[A-Z]{1,3})?[-\s]?\d{1,4}$/i;
  return pattern.test(v);
};

export const validateEmailOrMobile = (value = '', { mobileLength = 10 } = {}) => {
  const normalized = String(value).trim();
  const digitsOnly = stripNonDigits(normalized);
  const emailValid = isValidEmail(normalized);
  const mobileValid = isValidPhoneNumber(digitsOnly, { requiredLength: mobileLength });

  return {
    value: normalized,
    digits: digitsOnly,
    isEmail: emailValid,
    isMobile: mobileValid,
    isValid: emailValid || mobileValid,
  };
};

export const validatePasswordStrength = (password = '', options = {}) => {
  const {
    minLength = 8,
    requireLowercase = true,
    requireUppercase = true,
    requireNumber = true,
    requireSymbol = true,
    symbolRegex = DEFAULT_SYMBOL_REGEX,
  } = options;

  const normalized = String(password);

  const checks = {
    length: normalized.length >= minLength,
    lowercase: !requireLowercase || /[a-z]/.test(normalized),
    uppercase: !requireUppercase || /[A-Z]/.test(normalized),
    number: !requireNumber || /\d/.test(normalized),
    symbol: !requireSymbol || symbolRegex.test(normalized),
  };

  const failed = PASSWORD_REQUIREMENT_ORDER.filter((key) => checks[key] === false);

  return {
    isValid: failed.length === 0,
    checks,
    failed,
  };
};

/**
 * HSN and Tax form validation utilities
 */
export const validators = {
  /**
   * Validate HSN code field
   * @param {string} value - HSN code value
   * @returns {string} Error message or empty string if valid
   */
  hsnCode: (value) => {
    if (!value || !value.trim()) {
      return i18n.t('hsnCodes.forms.fields.hsnCode.errors.required');
    }

    const trimmedValue = value.trim();

    // HSN codes should be exactly 4, 6, or 8 digits
    if (!/^\d{4}$|^\d{6}$|^\d{8}$/.test(trimmedValue)) {
      return i18n.t('hsnCodes.forms.fields.hsnCode.errors.invalid');
    }

    return '';
  },

  /**
   * Validate HSN description field
   * @param {string} value - Description value
   * @returns {string} Error message or empty string if valid
   */
  hsnDescription: (value) => {
    if (!value || !value.trim()) {
      return i18n.t('hsnCodes.forms.fields.description.errors.required');
    }

    if (value.trim().length < 5) {
      return i18n.t('hsnCodes.forms.fields.description.errors.minLength');
    }

    return '';
  },

  /**
   * Validate tax ID field
   * @param {string|number} value - Tax ID value
   * @returns {string} Error message or empty string if valid
   */
  taxId: (value) => {
    if (!value) {
      return i18n.t('hsnCodes.forms.fields.tax.errors.required');
    }

    if (isNaN(parseInt(value))) {
      return i18n.t('hsnCodes.forms.fields.tax.errors.invalid');
    }

    return '';
  },

  /**
   * Validate tax rate field (for GST, CGST, SGST, IGST)
   * @param {string|number} value - Tax rate value
   * @param {string} fieldName - Name of the field for error messages
   * @returns {string} Error message or empty string if valid
   */
  taxRate: (value, fieldName) => {
    if (!value || !value.toString().trim()) {
      return i18n.t(`taxes.forms.fields.${fieldName}.errors.required`);
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      return i18n.t(`taxes.forms.fields.${fieldName}.errors.invalid`);
    }

    return '';
  },

  /**
   * Validate vehicle registration number
   * @param {string} value - Vehicle number
   * @returns {string} Error message or empty string if valid
   */
  vehicleNumber: (value) => {
    if (!value || !value.toString().trim()) {
      return i18n.t('common.form.startTruck.messages.vehicleRequired');
    }
    if (!isValidVehicleNumber(value)) {
      return i18n.t('common.form.startTruck.messages.invalidVehicleNumber');
    }
    return '';
  }
};

/**
 * Check for duplicate HSN descriptions in an existing list
 * @param {string} description - the description to check
 * @param {Array} existingList - array of existing HSN objects
 * @param {string|number|null} excludeId - optional id to exclude (when editing)
 * @returns {string} error message or empty string
 */
export const validateDuplicateHsnDescription = (description = '', existingList = [], excludeId = null) => {
  try {
    const desc = String(description || '').trim().toLowerCase();
    if (!desc) return '';

    const duplicate = (existingList || []).some(item => {
      if (!item || !item.hsn_description) return false;
      if (excludeId !== null && typeof item.id !== 'undefined' && String(item.id) === String(excludeId)) return false;
      return String(item.hsn_description).trim().toLowerCase() === desc;
    });

    if (duplicate) return i18n.t('hsnCodes.forms.fields.description.errors.duplicate');
    return '';
  } catch (err) {
    return '';
  }
};

/**
 * Validate HSN form data
 * @param {Object} formData - Form data object
 * @param {string} formData.hsn_code - HSN code
 * @param {string} formData.hsn_description - HSN description
 * @param {string|number} formData.tax_id - Tax ID
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const validateHsnForm = (formData) => {
  const errors = {};

  const hsnCodeError = validators.hsnCode(formData.hsn_code);
  if (hsnCodeError) {
    errors.hsn_code = hsnCodeError;
  }

  const descriptionError = validators.hsnDescription(formData.hsn_description);
  if (descriptionError) {
    errors.hsn_description = descriptionError;
  }

  const taxIdError = validators.taxId(formData.tax_id);
  if (taxIdError) {
    errors.tax_id = taxIdError;
  }

  return errors;
};

/**
 * Validate tax form data
 * @param {Object} formData - Form data object
 * @param {string|number} formData.gst - GST rate
 * @param {string|number} formData.cgst - CGST rate
 * @param {string|number} formData.sgst - SGST rate
 * @param {string|number} formData.igst - IGST rate
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const validateTaxForm = (formData) => {
  const errors = {};

  const taxFields = ['gst', 'cgst', 'sgst', 'igst'];

  taxFields.forEach(field => {
    const error = validators.taxRate(formData[field], field);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};

/**
 * Validate Product Size form data
 * @param {Object} formData - form fields from ProductSizeForm
 * @returns {Object} errors - map of field -> error message
 */
/**
 * A coil row nobody has typed into yet.
 *
 * Dropped silently instead of reported: an editor that always keeps one empty row on
 * screen would otherwise accuse the operator of leaving it blank.
 *
 * @param {Object} row
 * @returns {boolean}
 */
export const isBlankCoilRow = (row = {}) => (
  !String(row.batch_number ?? '').trim()
  && !String(row.weight_kg ?? '').trim()
  && !String(row.length_m ?? '').trim()
  && !String(row.colour ?? '').trim()
  && !String(row.supplier_batch_ref ?? '').trim()
);

/**
 * Validate ONE captured coil against the rules the API enforces.
 *
 * THE SINGLE SOURCE OF TRUTH for coil capture. Both coil editors call it — the
 * CoilEntrySheet used by purchase unloading, and the opening-stock block on the
 * product-size form. Two coil editors that disagree is the failure to avoid: a payload
 * one screen accepts and the server refuses from the other, discovered only on save.
 *
 * Weight is checked as a WHOLE number of kilograms because a database CHECK constraint
 * refuses anything else — a fraction dies as an IntegrityError deep inside a flush, so
 * it has to be stopped at the boundary.
 *
 * Batch numbers are unique per (warehouse, product size) on the server. `allRows` is
 * whatever list this coil belongs to, and only earlier rows are compared, so exactly one
 * row of a duplicate pair is flagged — the second one, which is the one to fix.
 *
 * @param {Object} row      the coil being checked
 * @param {number} index    its position in allRows
 * @param {Array}  allRows  the list it belongs to (one warehouse's coils, one sheet)
 * @returns {Object} field name -> message; empty when the coil is good
 */
export const validateCoilRow = (row = {}, index = 0, allRows = []) => {
  const rowErrors = {};

  const batch = String(row.batch_number ?? '').trim();
  if (!batch) {
    rowErrors.batch_number = i18n.t('coils.errors.batchRequired');
  } else {
    const duplicate = allRows.some((other, otherIndex) => (
      otherIndex < index
      && !isBlankCoilRow(other)
      && String(other.batch_number ?? '').trim().toLowerCase() === batch.toLowerCase()
    ));
    if (duplicate) rowErrors.batch_number = i18n.t('coils.errors.duplicateBatch', { batch });
  }

  const weightText = String(row.weight_kg ?? '').trim();
  const weight = parseFloat(weightText);
  if (!weightText || !Number.isFinite(weight) || weight <= 0) {
    rowErrors.weight_kg = i18n.t('coils.errors.weightRequired');
  } else if (!Number.isInteger(weight)) {
    rowErrors.weight_kg = i18n.t('coils.errors.wholeKgRequired');
  }

  const lengthText = String(row.length_m ?? '').trim();
  const length = parseFloat(lengthText);
  if (!lengthText || !Number.isFinite(length) || length <= 0) {
    rowErrors.length_m = i18n.t('coils.errors.lengthRequired');
  }

  if (!String(row.received_date ?? '').trim()) {
    rowErrors.received_date = i18n.t('coils.errors.receivedDateRequired');
  }

  return rowErrors;
};

/**
 * First problem in one warehouse's opening coils, as a sentence naming the coil.
 *
 * The product-size form has no room for a field-level error beside every coil, so the
 * warehouse card reports one message at a time, prefixed with the coil's position.
 *
 * @param {Array} coils
 * @returns {string|null} null when every coil is good (an empty list included)
 */
const firstCoilProblem = (coils = []) => {
  for (let index = 0; index < coils.length; index += 1) {
    const rowErrors = validateCoilRow(coils[index], index, coils);
    const [message] = Object.values(rowErrors);
    if (message) {
      return i18n.t('coils.errors.coilRowInvalid', { number: index + 1, message });
    }
  }
  return null;
};

export const validateProductSizeForm = (formData = {}) => {
  const errors = {};

  // A size is coil-tracked OR pieces-tracked, never both. Coil stock IS the kg sum
  // of that size's coils; track_by_pieces reinterprets the same balance as a piece
  // count. Together they leave the SKU with no coherent stock unit — a kg figure
  // labelled "(pcs)", two contradicting badges, and a transfer that is refused in
  // one breath and quantified in pieces in the next. The form disables each box
  // while the other is set; this backstops the paths that bypass it (CSV import).
  if (formData.coil_tracked && formData.track_by_pieces) {
    errors.track_by_pieces = i18n.t('pieces.productSize.trackByPieces.errors.coilConflict');
  }

  const alias = String(formData.alias_name || '').trim();
  const aliasRequiredMsg = i18n.t('productSizes.forms.fields.alias_name.errors.required');
  const aliasMinMsg = i18n.t('productSizes.forms.fields.alias_name.errors.minLength');
  if (!alias) {
    errors.alias_name = aliasRequiredMsg;
  } else if (alias.length < 2) {
    errors.alias_name = aliasMinMsg;
  }

  const size = String(formData.product_size || '').trim();
  const sizeRequiredMsg = i18n.t('productSizes.forms.fields.product_size.errors.required');
  if (!size) {
    errors.product_size = sizeRequiredMsg;
  }

  if (formData.product_weight && String(formData.product_weight).trim()) {
    const weight = parseFloat(String(formData.product_weight));
    if (Number.isNaN(weight) || weight < 0) {
      errors.product_weight = i18n.t('productSizes.forms.fields.product_weight.errors.invalid');
    }
  }

  if (formData.standard_length && String(formData.standard_length).trim()) {
    const length = parseFloat(String(formData.standard_length));
    if (Number.isNaN(length) || length < 0) {
      errors.standard_length = i18n.t('productSizes.forms.fields.product_thickness.errors.maxLength');
    }
  }

  if (formData.current_stock && String(formData.current_stock).trim()) {
    const stock = parseInt(String(formData.current_stock), 10);
    if (Number.isNaN(stock) || stock < 0) {
      errors.current_stock = i18n.t('productSizes.forms.fields.current_stock.errors.invalid');
    }
  }

  if (formData.minimum_stock && String(formData.minimum_stock).trim()) {
    const minStock = parseInt(String(formData.minimum_stock), 10);
    if (Number.isNaN(minStock) || minStock < 0) {
      errors.minimum_stock = i18n.t('productSizes.forms.fields.minimum_stock.errors.invalid');
    }
  }

  if (!formData.product_category_id) {
    errors.product_category_id = i18n.t('productSizes.forms.fields.category_id.errors.required');
  }

  if (!formData.product_catalogue_id) {
    errors.product_catalogue_id = i18n.t('productSizes.forms.fields.product_catalogue_id.errors.required');
  }

  if (formData.warehouses && Array.isArray(formData.warehouses)) {
    const coilTracked = !!formData.coil_tracked;

    formData.warehouses.forEach((w, idx) => {
      if (coilTracked) {
        // A coil-tracked warehouse's stock quantity is the sum of its coils: computed,
        // read-only, never typed, so there is nothing here to require. What has to hold
        // is that every coil is one the server will accept.
        //
        // An EMPTY coil list is legitimate and must not be blocked — opening stock for a
        // warehouse can genuinely be zero, and the size still needs to exist there.
        const coilProblem = firstCoilProblem(Array.isArray(w.coils) ? w.coils : []);
        if (coilProblem) {
          errors[`warehouses.${idx}.coils`] = coilProblem;
        }
        return;
      }

      if (w.stock_quantity === '' || w.stock_quantity === null || w.stock_quantity === undefined) {
        errors[`warehouses.${idx}.stock_quantity`] = "Stock quantity is required for all selected warehouses";
      }
    });
  }

  return errors;
};

/**
 * Validate new customer data for order form
 * @param {Object} customerData - New customer form data
 * @returns {Object} errors - map of field -> error message
 */
export const validateNewCustomerData = (customerData = {}) => {
  const errors = {};

  if (!customerData.owner_name || !String(customerData.owner_name).trim()) {
    errors.owner_name = i18n.t('customers.forms.fields.ownerName.errors.required');
  }

  if (!customerData.company_name || !String(customerData.company_name).trim()) {
    errors.company_name = i18n.t('customers.forms.fields.companyName.errors.required');
  }

  if (!customerData.tally_name || !String(customerData.tally_name).trim()) {
    errors.tally_name = i18n.t('customers.forms.fields.tallyName.errors.required');
  }
  
  // Conditionally require gst_number based on isAuthorized flag and sister_company status.
  // If isAuthorized is false OR sister_company is true, gst_number becomes optional.
  const isAuthorizedFlag = customerData.isAuthorized !== undefined ? customerData.isAuthorized : true; 
  const isSisterCompany = Boolean(customerData.sister_company);
  const isGstOptional = !isAuthorizedFlag || isSisterCompany;

  if (!isGstOptional && (!customerData.gst_number || !String(customerData.gst_number).trim())) {
    errors.gst_number = i18n.t('customers.forms.fields.gstNumber.errors.required');
  }

  if (!customerData.contact_person || !String(customerData.contact_person).trim()) {
    errors.contact_person = i18n.t('customers.forms.fields.contactPerson.errors.required');
  }

  if (!customerData.phone_number_1 || !String(customerData.phone_number_1).trim()) {
    errors.phone_number_1 = i18n.t('customers.forms.fields.phoneNumber1.errors.invalid');
  } else if (!isValidPhoneNumber(customerData.phone_number_1)) {
    errors.phone_number_1 = i18n.t('customers.forms.fields.phoneNumber1.errors.invalid');
  }

  if (!customerData.email || !String(customerData.email).trim()) {
    errors.email = i18n.t('customers.forms.fields.email.errors.invalid');
  } else if (!isValidEmail(customerData.email)) {
    errors.email = i18n.t('customers.forms.fields.email.errors.invalid');
  }

  if (!customerData.address_line1 || !String(customerData.address_line1).trim()) {
    errors.address_line1 = i18n.t('customers.forms.fields.address1.errors.required');
  }

  if (!customerData.city || !String(customerData.city).trim()) {
    errors.city = i18n.t('customers.forms.fields.city.errors.required');
  }

  if (!customerData.state || !String(customerData.state).trim()) {
    errors.state = i18n.t('customers.forms.fields.state.errors.required');
  }

  if (!customerData.country || !String(customerData.country).trim()) {
    errors.country = i18n.t('customers.forms.fields.country.errors.required');
  }

  if (!customerData.zip_code || !String(customerData.zip_code).trim()) {
    errors.zip_code = i18n.t('customers.forms.fields.zipCode.errors.required');
  }

  if (customerData.credit_period === null || customerData.credit_period === undefined || customerData.credit_period === '' || isNaN(Number(customerData.credit_period))) {
    errors.credit_period = i18n.t('customers.forms.fields.creditPeriod.errors.required');
  }

  return errors;
};

/**
 * Validate order items
 * @param {Array} items - Array of order items
 * @returns {Object} errors - map of field -> error message
 */
export const validateOrderItems = (items = [], { isEdit = false } = {}) => {
  const errors = {};

  // Check if at least one item is partially filled (has a product selected)
  const hasFilledItem = items.some(it => it.product_size_id);

  if (!Array.isArray(items) || items.length === 0 || !hasFilledItem) {
    errors.items = i18n.t('orders.forms.errors.itemsRequired');
    return errors;
  }

  items.forEach((item, idx) => {
    // Skip empty trailing rows (where no product is selected)
    // We consider it empty if it has no product_size_id and no quantity/price entered
    const isEmpty = !item.product_size_id && (!item.quantity || Number(item.quantity) === 0) && (!item.price || Number(item.price) === 0);
    if (isEmpty) return;

    if (!item.product_size_id) {
      errors[`items.${idx}.product_size_id`] = i18n.t('orders.forms.errors.itemProductSizeRequired');
    }
    // In edit mode, allow quantity 0 (lets users keep a line but zero it out instead of deleting).
    const qtyNum = Number(item.quantity);
    if (isPieceUnit(item.unit)) {
      // pcs: amount is priced on the piece count -> require pieces > 0. The
      // (weight) quantity is optional and may be 0/blank; only reject negatives.
      const qtyProvided = item.quantity !== '' && item.quantity !== null && item.quantity !== undefined;
      if (qtyProvided && (isNaN(qtyNum) || qtyNum < 0)) {
        errors[`items.${idx}.quantity`] = i18n.t('orders.forms.errors.itemQuantityRequired');
      }
      const pcs = Number(linePieceCount(item));
      const pcsBlank = linePieceCount(item) === '' || linePieceCount(item) === null || linePieceCount(item) === undefined;
      if (pcsBlank || isNaN(pcs) || pcs < 1) {
        errors[`items.${idx}.${linePiecesField(item)}`] = i18n.t('orders.forms.errors.itemPiecesRequired');
      }
    } else {
      const qtyInvalid = item.quantity === '' || item.quantity === null || item.quantity === undefined || isNaN(qtyNum) || (isEdit ? qtyNum < 0 : qtyNum <= 0);
      if (qtyInvalid) {
        errors[`items.${idx}.quantity`] = i18n.t('orders.forms.errors.itemQuantityRequired');
      }
    }
    // Price is OPTIONAL (migration 061): a blank price = unpriced line. Only
    // validate it when a value is actually entered (reject NaN / negative).
    if (item.price !== '' && item.price !== null && item.price !== undefined) {
      if (isNaN(Number(item.price)) || Number(item.price) < 0) {
        errors[`items.${idx}.price`] = i18n.t('orders.forms.errors.itemPriceInvalid');
      }
    }
    // Validate unit (required)
    if (!item.unit || !String(item.unit).trim()) {
      errors[`items.${idx}.unit`] = i18n.t('orders.forms.errors.itemUnitRequired');
    }
    // Validate length (required and numeric)
    if (item.length === null || item.length === undefined || String(item.length).trim() === '') {
      errors[`items.${idx}.length`] = i18n.t('orders.forms.errors.itemLengthRequired');
    } else {
      const numLen = parseFloat(String(item.length));
      if (Number.isNaN(numLen) || numLen < 0) {
        errors[`items.${idx}.length`] = i18n.t('orders.forms.errors.itemLengthInvalid');
      }
    }

    // Coil cut-pieces (how many pieces to cut the coil into). Only for
    // coil-tracked lines — the form tags them with a transient `__coilTracked`
    // flag (both the global toggle AND the per-SKU flag). OPTIONAL (a coil may be
    // sold uncut, and legacy orders predate the field), but WHEN GIVEN it must be
    // a positive whole number and at least the number of coils on the line: you
    // cannot cut N coils into fewer than N pieces.
    if (item.__coilTracked) {
      const raw = item.cut_pieces;
      const missing = raw === '' || raw === null || raw === undefined;
      if (!missing) {
        const coilCount = Array.isArray(item.coil_picks) ? item.coil_picks.length : 0;
        const floor = Math.max(1, coilCount);
        const cutNum = Number(raw);
        if (Number.isNaN(cutNum) || !Number.isInteger(cutNum) || cutNum < 1) {
          errors[`items.${idx}.cut_pieces`] = i18n.t('coils.line.cutPiecesInteger');
        } else if (cutNum < floor) {
          errors[`items.${idx}.cut_pieces`] = i18n.t('coils.line.cutPiecesMinCoils', { count: String(floor) });
        }
      }
    }
  });

  return errors;
};

/**
 * Validate order form data for existing customer
 * @param {Object} formOrder - Order form data
 * @returns {Object} errors - map of field -> error message
 */
export const validateExistingCustomerOrder = (formOrder = {}, { isEdit = false } = {}) => {
  const errors = {};

  if (!formOrder.customer_id) {
    errors.customer_id = i18n.t('orders.forms.errors.customerRequired');
  }

  if (!formOrder.location_id) {
    errors.location_id = i18n.t('orders.forms.errors.locationRequired');
  }

  if (!isEdit && !String(formOrder.freight_type || '').trim()) {
    errors.freight_type = i18n.t(
      'common.formFields.placeholders.selectFreightType'
    );
  }

  // Validate numeric and string typed order-level fields
  if (formOrder.freight !== null && formOrder.freight !== undefined && String(formOrder.freight).trim() !== '') {
    const num = parseFloat(String(formOrder.freight));
    if (Number.isNaN(num)) {
      errors.freight = i18n.t('orders.forms.errors.freightInvalid');
    }
  }

  if (formOrder.discount_type !== null && formOrder.discount_type !== undefined && String(formOrder.discount_type).trim() !== '') {
    const dt = String(formOrder.discount_type).trim();
    if (!ALLOWED_DISCOUNT_TYPES.includes(dt)) {
      errors.discount_type = i18n.t('orders.forms.errors.discountTypeInvalid');
    }
  }

  if (formOrder.discount_value !== null && formOrder.discount_value !== undefined && String(formOrder.discount_value).trim() !== '') {
    const num = parseFloat(String(formOrder.discount_value));
    if (Number.isNaN(num)) {
      errors.discount_value = i18n.t('orders.forms.errors.discountValueInvalid');
    }
  }

  if (formOrder.loading_cost_per_kg !== null && formOrder.loading_cost_per_kg !== undefined && String(formOrder.loading_cost_per_kg).trim() !== '') {
    const num = parseFloat(String(formOrder.loading_cost_per_kg));
    if (Number.isNaN(num)) {
      errors.loading_cost_per_kg = i18n.t('orders.forms.errors.loadingCostInvalid');
    }
  }

  if (formOrder.gst_option !== null && formOrder.gst_option !== undefined && String(formOrder.gst_option).trim() !== '') {
    const go = String(formOrder.gst_option).trim();
    if (!ALLOWED_GST_OPTIONS.includes(go)) {
      errors.gst_option = i18n.t('orders.forms.errors.gstOptionInvalid');
    }
  }

  // If GST option requires GSTN, ensure GSTN number provided
  if (String(formOrder.gst_option).trim() === 'with_gstn') {
    if (!formOrder.gstn_number || !String(formOrder.gstn_number).trim()) {
      errors.gstn_number = i18n.t('orders.forms.errors.gstnNumberInvalid');
    }
  }

  // If GST option is percentage, ensure gst_percentage is a valid number
  if (String(formOrder.gst_option).trim() === 'percentage') {
    if (formOrder.gst_percentage === null || formOrder.gst_percentage === undefined || String(formOrder.gst_percentage).trim() === '') {
      errors.gst_percentage = i18n.t('orders.forms.errors.gstPercentageInvalid');
    } else {
      const num = parseFloat(String(formOrder.gst_percentage));
      if (Number.isNaN(num)) {
        errors.gst_percentage = i18n.t('orders.forms.errors.gstPercentageInvalid');
      }
    }
  }



  // Validate items
  const itemErrors = validateOrderItems(formOrder.items, { isEdit });
  Object.assign(errors, itemErrors);

  // If GST option requires GSTN, ensure GSTN number provided
  if (String(formOrder.gst_option).trim() === 'with_gstn') {
    if (!formOrder.gstn_number || !String(formOrder.gstn_number).trim()) {
      errors.gstn_number = i18n.t('orders.forms.errors.gstnNumberInvalid');
    }
  }

  // If GST option is percentage, ensure gst_percentage is a valid number
  if (String(formOrder.gst_option).trim() === 'percentage') {
    if (formOrder.gst_percentage === null || formOrder.gst_percentage === undefined || String(formOrder.gst_percentage).trim() === '') {
      errors.gst_percentage = i18n.t('orders.forms.errors.gstPercentageInvalid');
    } else {
      const num = parseFloat(String(formOrder.gst_percentage));
      if (Number.isNaN(num)) {
        errors.gst_percentage = i18n.t('orders.forms.errors.gstPercentageInvalid');
      }
    }
  }

  if (formOrder.expected_delivery_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = formOrder.expected_delivery_date.split('-').map(Number);
    const inputDate = new Date(y, m - 1, d);
    if (inputDate < today) {
      errors.expected_delivery_date = "Expected delivery date cannot be less than today's date";
    }
  }

  return errors;
};

/**
 * Validate order form data for new customer
 * @param {Object} formOrder - Order form data
 * @param {Object} newCustomer - New customer data
 * @returns {Object} errors - map of field -> error message
 */
export const validateNewCustomerOrder = (formOrder = {}, newCustomer = {}, { isEdit = false } = {}) => {
  const errors = {};

  // Validate new customer data
  const customerErrors = validateNewCustomerData(newCustomer);
  Object.assign(errors, customerErrors);

  // Validate order-specific fields
  if (!formOrder.location_id) {
    errors.location_id = i18n.t('orders.forms.errors.locationRequired');
  }

  if (!isEdit && !String(formOrder.freight_type || '').trim()) {
    errors.freight_type = i18n.t(
      'common.formFields.placeholders.selectFreightType'
    );
  }


  // Validate items
  const itemErrors = validateOrderItems(formOrder.items, { isEdit });
  Object.assign(errors, itemErrors);

  // Validate numeric and string typed order-level fields (same checks as existing customer)
  if (formOrder.freight !== null && formOrder.freight !== undefined && String(formOrder.freight).trim() !== '') {
    const num = parseFloat(String(formOrder.freight));
    if (Number.isNaN(num)) {
      errors.freight = i18n.t('orders.forms.errors.freightInvalid');
    }
  }

  if (formOrder.discount_type !== null && formOrder.discount_type !== undefined && String(formOrder.discount_type).trim() !== '') {
    const dt = String(formOrder.discount_type).trim();
    if (!ALLOWED_DISCOUNT_TYPES.includes(dt)) {
      errors.discount_type = i18n.t('orders.forms.errors.discountTypeInvalid');
    }
  }

  if (formOrder.discount_value !== null && formOrder.discount_value !== undefined && String(formOrder.discount_value).trim() !== '') {
    const num = parseFloat(String(formOrder.discount_value));
    if (Number.isNaN(num)) {
      errors.discount_value = i18n.t('orders.forms.errors.discountValueInvalid');
    }
  }

  if (formOrder.loading_cost_per_kg !== null && formOrder.loading_cost_per_kg !== undefined && String(formOrder.loading_cost_per_kg).trim() !== '') {
    const num = parseFloat(String(formOrder.loading_cost_per_kg));
    if (Number.isNaN(num)) {
      errors.loading_cost_per_kg = i18n.t('orders.forms.errors.loadingCostInvalid');
    }
  }

  if (formOrder.gst_option !== null && formOrder.gst_option !== undefined && String(formOrder.gst_option).trim() !== '') {
    const go = String(formOrder.gst_option).trim();
    if (!ALLOWED_GST_OPTIONS.includes(go)) {
      errors.gst_option = i18n.t('orders.forms.errors.gstOptionInvalid');
    }
  }



  if (formOrder.expected_delivery_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = formOrder.expected_delivery_date.split('-').map(Number);
    const inputDate = new Date(y, m - 1, d);
    if (inputDate < today) {
      errors.expected_delivery_date = "Expected delivery date cannot be less than today's date";
    }
  }

  return errors;
};

/**
 * Input filters and formatters
 */
export const inputFilters = {
  /**
   * Filter for HSN code input - allows only digits and enforces max length
   * @param {string} value - Input value
   * @returns {string} Filtered value
   */
  hsnCode: (value) => {
    // Allow only digits and limit to 8 characters
    return value.replace(/\D/g, '').slice(0, 8);
  },

  /**
   * Filter for numeric input - allows only numbers and decimal point
   * @param {string} value - Input value
   * @returns {string} Filtered value
   */
  numeric: (value) => {
    // Allow only numbers and decimal point
    return value.replace(/[^\d.]/g, '');
  },

  /**
   * Filter for integer input - allows only digits
   * @param {string} value - Input value
   * @returns {string} Filtered value
   */
  integer: (value) => {
    // Allow only digits
    return value.replace(/\D/g, '');
  }
};


/**
 * Validate inquiry items
 * @param {Array} items - Array of inquiry items
 * @returns {Object} errors - map of field -> error message
 */
export const validateInquiryItems = (items = []) => {
  const errors = {};

  // Check if at least one item is partially filled (has a product selected)
  const hasFilledItem = items.some(it => it.product_size_id);

  if (!Array.isArray(items) || items.length === 0 || !hasFilledItem) {
    errors.items = i18n.t('orders.forms.errors.itemsRequired');
    return errors;
  }

  items.forEach((item, idx) => {
    // Skip empty trailing rows
    const isEmpty = !item.product_size_id && (!item.quantity || Number(item.quantity) === 0) && (!item.price || Number(item.price) === 0);
    if (isEmpty) return;

    if (!item.product_size_id) {
      errors[`items.${idx}.product_size_id`] = i18n.t('orders.forms.errors.itemProductSizeRequired');
    }
    if (isPieceUnit(item.unit || item.units)) {
      // pcs: require piece count > 0; quantity (weight) optional, reject only negatives.
      const qtyProvided = item.quantity !== '' && item.quantity !== null && item.quantity !== undefined;
      if (qtyProvided && (isNaN(Number(item.quantity)) || Number(item.quantity) < 0)) {
        errors[`items.${idx}.quantity`] = i18n.t('orders.forms.errors.itemQuantityRequired');
      }
      const pcs = Number(linePieceCount(item));
      const pcsBlank = linePieceCount(item) === '' || linePieceCount(item) === null || linePieceCount(item) === undefined;
      if (pcsBlank || isNaN(pcs) || pcs < 1) {
        errors[`items.${idx}.${linePiecesField(item)}`] = i18n.t('orders.forms.errors.itemPiecesRequired');
      }
    } else if (!item.quantity || isNaN(Number(item.quantity)) || Number(item.quantity) <= 0) {
      errors[`items.${idx}.quantity`] = i18n.t('orders.forms.errors.itemQuantityRequired');
    } else {
      const parts = String(item.quantity).split('.');
      if (parts.length > 1 && parts[1].length > 4) {
        errors[`items.${idx}.quantity`] = 'Quantity cannot have more than 4 decimal places';
      }
    }
    // Price is OPTIONAL (migration 061): a blank price = unpriced line. Only
    // validate it when a value is actually entered.
    if (item.price !== '' && item.price !== null && item.price !== undefined) {
      if (isNaN(Number(item.price))) {
        errors[`items.${idx}.price`] = i18n.t('orders.forms.errors.itemPriceInvalid');
      } else if (Number(item.price) < 0) {
        errors[`items.${idx}.price`] = 'Price cannot be negative';
      } else {
        const parts = String(item.price).split('.');
        if (parts.length > 1 && parts[1].length > 4) {
          errors[`items.${idx}.price`] = 'Price cannot have more than 4 decimal places';
        }
      }
    }
    // units is optional in InquiryForm

    // Coil cut-pieces (how many pieces to cut the coil into). Only for
    // coil-tracked lines — the form tags them with `__coilTracked`. OPTIONAL, but
    // WHEN GIVEN must be a positive whole number. Inquiries never pick coils, so
    // there is no coil-count floor beyond 1.
    if (item.__coilTracked) {
      const raw = item.cut_pieces;
      const missing = raw === '' || raw === null || raw === undefined;
      if (!missing) {
        const cutNum = Number(raw);
        if (Number.isNaN(cutNum) || !Number.isInteger(cutNum) || cutNum < 1) {
          errors[`items.${idx}.cut_pieces`] = i18n.t('coils.line.cutPiecesInteger');
        }
      }
    }
  });

  return errors;
};

/**
 * Validate inquiry form data for existing customer
 * @param {Object} formOrder - Inquiry form data
 * @returns {Object} errors - map of field -> error message
 */
export const validateExistingCustomerInquiry = (formOrder = {}) => {
  const errors = {};

  if (!formOrder.customer_id) {
    errors.customer_id = i18n.t('orders.forms.errors.customerRequired');
  }

  if (!formOrder.location_id) {
    errors.location_id = i18n.t('orders.forms.errors.locationRequired');
  }

  if (formOrder.freight !== null && formOrder.freight !== undefined && String(formOrder.freight).trim() !== '') {
    const num = parseFloat(String(formOrder.freight));
    if (Number.isNaN(num)) {
      errors.freight = i18n.t('orders.forms.errors.freightInvalid');
    }
  }

  if (formOrder.expected_delivery_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = formOrder.expected_delivery_date.split('-').map(Number);
    const inputDate = new Date(y, m - 1, d);
    if (inputDate < today) {
      errors.expected_delivery_date = "Expected delivery date cannot be less than today's date";
    }
  }

  // Validate items
  const itemErrors = validateInquiryItems(formOrder.items);
  Object.assign(errors, itemErrors);

  return errors;
};

/**
 * Validate inquiry form data for new customer
 * @param {Object} formOrder - Inquiry form data
 * @param {Object} newCustomer - New customer data
 * @returns {Object} errors - map of field -> error message
 */
export const validateNewCustomerInquiry = (formOrder = {}, newCustomer = {}) => {
  const errors = {};

  // Validate new customer data
  const customerErrors = validateNewCustomerData(newCustomer);
  Object.assign(errors, customerErrors);

  // Validate order-specific fields
  if (!formOrder.location_id) {
    errors.location_id = i18n.t('orders.forms.errors.locationRequired');
  }

  // Validate items
  const itemErrors = validateInquiryItems(formOrder.items);
  Object.assign(errors, itemErrors);

  if (formOrder.freight !== null && formOrder.freight !== undefined && String(formOrder.freight).trim() !== '') {
    const num = parseFloat(String(formOrder.freight));
    if (Number.isNaN(num)) {
      errors.freight = i18n.t('orders.forms.errors.freightInvalid');
    }
  }

  if (formOrder.expected_delivery_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = formOrder.expected_delivery_date.split('-').map(Number);
    const inputDate = new Date(y, m - 1, d);
    if (inputDate < today) {
      errors.expected_delivery_date = "Expected delivery date cannot be less than today's date";
    }
  }

  return errors;
};
const validateUtils = {
  stripNonDigits,
  formatNumericInput,
  isValidEmail,
  isValidPhoneNumber,
  isValidVehicleNumber,
  validateEmailOrMobile,
  validatePasswordStrength,
  validators,
  validateHsnForm,
  validateTaxForm,
  validateProductSizeForm,
  validateCoilRow,
  isBlankCoilRow,
  validateNewCustomerData,
  validateOrderItems,
  validateExistingCustomerOrder,
  validateNewCustomerOrder,
  inputFilters,
  PASSWORD_REQUIREMENT_ORDER,
  validateInquiryItems,
  validateExistingCustomerInquiry,
  validateNewCustomerInquiry,
};

export default validateUtils;
